import {
  TableColumns,
  TableColumnsColumnProps,
  Tag,
  Text,
} from '@mercai/clever'
import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { formatCurrency, formatDate } from '@/helpers'
import { SupplyPaymentProps, SupplyPaymentStatusEnum } from '@/types'

import { TableSupplyPaymentsProps } from './table-supply-payments.type'

export const TableSupplyPayments = ({
  data,
  isLoading = false,
  actions,
}: TableSupplyPaymentsProps) => {
  const { t: tEnums } = useTranslation('enums')
  const { t } = useTranslation('screenSupplyOrderDetail')

  const statuses: Record<SupplyPaymentStatusEnum, ReactNode> = {
    pending: (
      <Tag variant="warning" model="light">
        {tEnums('supply_order_payment_status_pending')}
      </Tag>
    ),
    payment_required: (
      <Tag variant="warning" model="light">
        {tEnums('supply_order_payment_status_payment_required')}
      </Tag>
    ),
    paid: (
      <Tag variant="success" model="light">
        {tEnums('supply_order_payment_status_paid')}
      </Tag>
    ),
    cancelled: (
      <Tag variant="danger" model="light">
        {tEnums('supply_order_payment_status_cancelled')}
      </Tag>
    ),
  }

  const columns: TableColumnsColumnProps<SupplyPaymentProps>[] = [
    {
      title: t('tableSupplyPaymentsExpectedPaymentDateColumn'),
      key: 'expectedPaymentDate',
      render: ({ record }) => (
        <Text>
          {record?.expectedPaymentDate
            ? formatDate(record?.expectedPaymentDate)
            : '-'}
        </Text>
      ),
    },
    {
      title: t('tableSupplyPaymentsValueColumn'),
      key: 'value',
      render: ({ record }) => (
        <Text>
          {record?.value
            ? formatCurrency(record?.value, 'es-MX', record?.currency)
            : '-'}
        </Text>
      ),
    },
    {
      title: t('tableSupplyPaymentsCurrencyColumn'),
      key: 'currency',
      render: ({ record }) => <Text>{record?.currency || '-'}</Text>,
    },
    {
      title: t('tableSupplyPaymentsBankColumn'),
      key: 'bank',
      render: ({ record }) => (
        <Text>
          {record?.statements?.[0]?.transaction?.destinationBank || '-'}
        </Text>
      ),
    },
    {
      title: t('tableSupplyPaymentsStatusColumn'),
      key: 'status',
      render: ({ record }) =>
        record?.status ? <>{statuses[record.status]}</> : '-',
    },
    {
      title: t('tableSupplyPaymentsPaymentPriorityColumn'),
      key: 'paymentPriority',
      render: ({ record }) => (
        <Text>
          {record?.paymentPriority
            ? tEnums(`supply_payment_priority_${record?.paymentPriority}`)
            : '-'}
        </Text>
      ),
    },
    {
      title: t('tableSupplyPaymentsTypeColumn'),
      key: 'type',
      render: ({ record }) => (
        <Text>
          {record?.type
            ? tEnums(`supply_order_payment_type_${record?.type}`)
            : '-'}
        </Text>
      ),
    },
    ...(actions
      ? [
          {
            title: t('tableSupplyPaymentsActionsColumn'),
            ...actions,
            key: actions?.key || 'actions',
          },
        ]
      : []),
  ]

  return (
    <div>
      <TableColumns columns={columns} data={data || []} isLoading={isLoading} />
    </div>
  )
}
